import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
	removeAllPhotonPathsFromScene,
	showPhotonPathOnHover,
	showPhotonPathsForHeatmapCell,
} from './rayTracing';
import { Forma } from 'forma-embedded-view-sdk/auto';
import { useDailySunlightStore } from '@/store/dailySunlightsStore';
import { useViewFeaturesStore } from '@/store/viewFeaturesStore';
import { updateWindowDeletionHighlight } from './geometryModification';
import { showTerrainFeatureTooltip as showTerrainTooltipSprite, removeTerrainFeatureTooltip as removeTerrainTooltipSprite } from './viewFeatures';
import { unitConversionService } from '@/services/unit-conversion.service';
import { useThreeSceneStore } from '@/store/threeSceneStore';
import { DailySunlightResult } from '@/types/sunlight';
import { handleAddSunlightScoreModal } from '@/components/AddSunlightScoreModal/AddSunlightScoreModal';
import { WorkflowProps } from '@/types/workflow';
import { handleAddViewScoreModal } from '@/components/AddViewScoreModal/AddViewScoreModal';
import { PhotonHeatmapCellData } from './rayTracing';

export interface CameraConfig {
	fov: number;
	near: number;
	far: number;
	initialPosition: { x: number; y: number; z: number };
}

export interface ControlsConfig {
	enableDamping: boolean;
	dampingFactor: number;
	screenSpacePanning: boolean;
	minDistance: number;
	maxDistance: number;
	rotationSensitivity: number;
	keyboardPanSpeed: number;
}

export interface MouseHandlers {
	handleMouseDown: (event: MouseEvent) => void;
	handleMouseMove: (event: MouseEvent) => void;
	handleMouseUp: () => void;
	handleMouseLeave: () => void;
	getIsRotating: () => boolean;
}

export interface KeyboardHandlers {
	handleKeyDown: (event: KeyboardEvent) => void;
	handleKeyUp: (event: KeyboardEvent) => void;
	cleanup: () => void;
}

export interface ResizeHandler {
	handleResize: () => void;
	cleanup: () => void;
}

export const DEFAULT_CAMERA_CONFIG: CameraConfig = {
	fov: 50,
	near: 0.1,
	far: 10000,
	initialPosition: { x: 100, y: 500, z: 500 },
} as const;

export const DEFAULT_CONTROLS_CONFIG: ControlsConfig = {
	enableDamping: true,
	dampingFactor: 0.1,
	screenSpacePanning: false,
	minDistance: 0.001,
	maxDistance: 5000,
	rotationSensitivity: 0.01,
	keyboardPanSpeed: 1.0,
} as const;

const getFormaCamera = async () => {
	const camera = await Forma.camera.getCurrent();
	return camera;
};

/**
 * Creates and configures a perspective camera
 */
export const setupCamera = async (
	width: number,
	height: number,
	config: CameraConfig = DEFAULT_CAMERA_CONFIG
): Promise<THREE.PerspectiveCamera> => {
	const formaCamera = await getFormaCamera();
	const camera = new THREE.PerspectiveCamera(config.fov, width / height, config.near, config.far);

	camera.position.set(formaCamera.position.x, formaCamera.position.y, formaCamera.position.z);
	camera.lookAt(formaCamera.target.x, formaCamera.target.y, formaCamera.target.z);
	camera.up.set(0, 0, 1); // Z-up coordinate system

	return camera;
};

/**
 * Creates and configures OrbitControls
 */
export const setupControls = (
	camera: THREE.Camera,
	renderer: THREE.WebGLRenderer,
	config: ControlsConfig = DEFAULT_CONTROLS_CONFIG
): OrbitControls => {
	const controls = new OrbitControls(camera, renderer.domElement);

	controls.enableDamping = config.enableDamping;
	controls.dampingFactor = config.dampingFactor;
	controls.screenSpacePanning = config.screenSpacePanning;
	controls.minDistance = config.minDistance;
	controls.maxDistance = config.maxDistance;

	controls.enableZoom = true;
	controls.zoomToCursor = true;
	controls.enablePan = false;
	controls.enableRotate = true;

	controls.mouseButtons = {
		LEFT: undefined,
		MIDDLE: THREE.MOUSE.DOLLY,
		RIGHT: THREE.MOUSE.ROTATE,
	};

	controls.touches = {
		ONE: undefined,
		TWO: THREE.TOUCH.DOLLY_ROTATE,
	};

	// Store controls reference in camera userData and track rotation state
	camera.userData.controls = controls;
	camera.userData.isRotating = false;
	camera.userData.rightClickTimeout = null;

	// Track rotation state via OrbitControls events
	controls.addEventListener('start', () => {
		camera.userData.isRotating = true;
	});

	controls.addEventListener('end', () => {
		camera.userData.isRotating = false;
	});

	return controls;
};

/**
 * Creates mouse event handlers for camera interaction
 */
export const createMouseHandlers = (
	renderer: THREE.WebGLRenderer,
	scene: THREE.Scene,
	camera: THREE.Camera,
	workflowProps: WorkflowProps
): MouseHandlers => {
	let initialCameraPosition: THREE.Vector3 | null = null;
	let initialCameraQuaternion: THREE.Quaternion | null = null;
	
	const isCameraRotating = (): boolean => {
		return camera.userData.isRotating || false;
	};

	const handleContextMenuAction = (mouse: THREE.Vector2) => {
		useDailySunlightStore.getState().setHoveredGridPointData(null);
		useDailySunlightStore.getState().setDetailedGridPointsData([]);
		useDailySunlightStore.getState().setHoveredFacadeData(null);
		useDailySunlightStore.getState().setFacadeWallData([]);
		removeGridPointHighlight(scene);
		removeHeatmapTooltip(scene);
		removeAllPhotonPathsFromScene(scene);

		// Create raycaster
		const raycaster = new THREE.Raycaster();
		raycaster.setFromCamera(mouse, camera);
		raycaster.params.Points = { threshold: 0.5 };

		// Find sunlight visualization objects
		const roomSunlightVisualization: THREE.Object3D[] = [];
		scene.traverse((child) => {
			if (
				child.userData.isRoomSunlightVisualization ||
				child.userData.isSelectedRegionVisualization
			) {
				roomSunlightVisualization.push(child);
			}
		});

		if (roomSunlightVisualization.length === 0) {
			// No sunlight grids, check for windows instead
			const windowObjects: THREE.Object3D[] = [];
			scene.traverse((child) => {
				if (child.userData.isWindow) {
					windowObjects.push(child);
				}
			});

			if (windowObjects.length > 0) {
				const windowIntersects = raycaster.intersectObjects(windowObjects, true);
				if (windowIntersects.length > 0) {
					// Find the window group (parent with isWindow userData)
					let windowGroup = windowIntersects[0].object;
					while (windowGroup.parent && !windowGroup.userData.isWindow) {
						windowGroup = windowGroup.parent;
					}
					if (windowGroup.userData.isWindow) {
						// Find closest roomFillMesh to window position
						const windowPosition = new THREE.Vector3();
						windowGroup.getWorldPosition(windowPosition);
						
						let closestRoomFillMesh: THREE.Mesh | null = null;
						let closestDistance = Infinity;
						
						scene.traverse((child) => {
							if (child.userData.isRoomFloorFill && child instanceof THREE.Mesh) {
								const distance = child.position.distanceTo(windowPosition);
								if (distance < closestDistance) {
									closestDistance = distance;
									closestRoomFillMesh = child;
								}
							}
						});

							handleAddViewScoreModal(closestRoomFillMesh, windowGroup, windowPosition, workflowProps);
						return;
					}
				}
			}
			return;
		}

		// Intersect with sunlight grid
		const intersects = raycaster.intersectObjects(roomSunlightVisualization, false);

		for (const intersection of intersects) {
			if (intersection.instanceId === undefined) continue;

			const instancedMesh = intersection.object as THREE.InstancedMesh;
			const instanceId = intersection.instanceId;

			// Check if point is clipped
			const matrix = new THREE.Matrix4();
			instancedMesh.getMatrixAt(instanceId, matrix);
			const position = new THREE.Vector3();
			position.setFromMatrixPosition(matrix);

			const material = instancedMesh.material as THREE.Material & {
				clippingPlanes?: THREE.Plane[];
			};

			let isClipped = false;
			if (material && Array.isArray(material.clippingPlanes)) {
				isClipped = material.clippingPlanes.some(
					(plane: THREE.Plane) => plane.distanceToPoint(position) < 0
				);
			}

			if (!isClipped) {
				const sunlightData = instancedMesh.userData.gridPointsData?.[instanceId] as DailySunlightResult | undefined;

				if (sunlightData && sunlightData.roomName) {
					// Open modal with room name
					const roomFillMesh = scene.getObjectByName(sunlightData.roomName) as THREE.Mesh;
					handleAddSunlightScoreModal(sunlightData.roomName, roomFillMesh, workflowProps);
					return;
				}
			}
		}
	};

	const hasCameraMoved = (): boolean => {
		if (!initialCameraPosition || !initialCameraQuaternion) return false;
		
		const positionDelta = camera.position.distanceTo(initialCameraPosition);
		const rotationDelta = camera.quaternion.angleTo(initialCameraQuaternion);
		
		// Threshold: if camera moved more than 0.1 units or rotated more than 0.01 radians
		return positionDelta > 0.1 || rotationDelta > 0.01;
	};

	const handleMouseDown = (event: MouseEvent) => {
		// Only handle right-click (button 2)
		if (event.button !== 2) return;
		event.preventDefault();
		if (camera.userData.rightClickTimeout) {
			clearTimeout(camera.userData.rightClickTimeout);
			camera.userData.rightClickTimeout = null;
		}

		initialCameraPosition = camera.position.clone();
		initialCameraQuaternion = camera.quaternion.clone();

		const rect = renderer.domElement.getBoundingClientRect();
		const mouse = new THREE.Vector2();
		mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
		mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

		camera.userData.rightClickTimeout = setTimeout(() => {
			// Only execute if camera hasn't moved (user didn't rotate)
			if (!hasCameraMoved()) {
				handleContextMenuAction(mouse);
			}
			camera.userData.rightClickTimeout = null;
		}, 100);
	};

	const handleMouseUp = () => {
		if (camera.userData.rightClickTimeout) {
			clearTimeout(camera.userData.rightClickTimeout);
			camera.userData.rightClickTimeout = null;
		}
	};

	const handleMouseLeave = () => {
		useDailySunlightStore.getState().setHoveredGridPointData(null);
		removeHeatmapTooltip(scene);
	};

	const createHeatmapTooltipTexture = (data: PhotonHeatmapCellData): THREE.CanvasTexture => {
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d')!;
		canvas.width = 560;
		canvas.height = 320;

		context.clearRect(0, 0, canvas.width, canvas.height);
		const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
		gradient.addColorStop(0, 'rgba(10, 14, 22, 0.96)');
		gradient.addColorStop(1, 'rgba(22, 36, 52, 0.96)');
		context.fillStyle = gradient;

		const radius = 18;
		context.beginPath();
		context.moveTo(radius, 0);
		context.lineTo(canvas.width - radius, 0);
		context.quadraticCurveTo(canvas.width, 0, canvas.width, radius);
		context.lineTo(canvas.width, canvas.height - radius);
		context.quadraticCurveTo(canvas.width, canvas.height, canvas.width - radius, canvas.height);
		context.lineTo(radius, canvas.height);
		context.quadraticCurveTo(0, canvas.height, 0, canvas.height - radius);
		context.lineTo(0, radius);
		context.quadraticCurveTo(0, 0, radius, 0);
		context.closePath();
		context.fill();

		context.strokeStyle = 'rgba(125, 211, 252, 0.35)';
		context.lineWidth = 4;
		context.stroke();

		context.textBaseline = 'top';
		context.fillStyle = '#d7f0ff';
		context.font = 'bold 38px Arial';
		context.fillText('Energy Heatmap Cell', 26, 22);

		context.font = '28px Arial';
		context.fillStyle = '#b8c7d9';
		const groupLabel =
			data.group === 'facade'
				? 'Facade'
				: data.group === 'roof'
					? 'Roof'
					: data.group === 'terrain'
						? 'Terrain'
						: 'Floor';
		context.fillText(`Type: ${groupLabel}`, 26, 82);
			context.fillText(`Irradiance: ${data.irradianceWm2.toFixed(2)} W/m2`, 26, 122);
			context.fillText(`Illuminance: ${data.illuminanceLux.toFixed(0)} lux`, 26, 162);
			context.fillText(`Photons: ${data.photonCount.toLocaleString()}`, 26, 202);
			context.fillText(`Cell size: ${data.cellSize}m`, 26, 242);

		context.fillStyle = '#8fe388';
		context.fillText(`Normalized: ${(data.normalizedEnergy * 100).toFixed(0)}%`, 26, 282);
		context.fillStyle = '#b8c7d9';

		const texture = new THREE.CanvasTexture(canvas);
		texture.needsUpdate = true;
		return texture;
	};

	const showHeatmapTooltip = (
		scene: THREE.Scene,
		camera: THREE.Camera,
		position: THREE.Vector3,
		data: PhotonHeatmapCellData
	): void => {
		removeHeatmapTooltip(scene);
		const texture = createHeatmapTooltipTexture(data);
		const material = new THREE.SpriteMaterial({
			map: texture,
			transparent: true,
			opacity: 1,
			depthTest: false,
			depthWrite: false,
			sizeAttenuation: true,
		});
		const sprite = new THREE.Sprite(material);
		const distanceToCamera = camera.position.distanceTo(position);
		const scale = distanceToCamera * 0.12;
		sprite.scale.set(scale * 2.15, scale, 1);
		sprite.position.copy(position).add(new THREE.Vector3(0, 0, Math.max(1, distanceToCamera * 0.03)));
		sprite.name = '__heatmapTooltip';
		sprite.userData.isHeatmapTooltip = true;
		sprite.renderOrder = 2100;
		scene.add(sprite);
	};

	const removeHeatmapTooltip = (scene: THREE.Scene): void => {
		const tooltip = scene.getObjectByName('__heatmapTooltip');
		if (!tooltip) return;
		scene.remove(tooltip);
		if (tooltip instanceof THREE.Sprite && tooltip.material instanceof THREE.SpriteMaterial) {
			if (tooltip.material.map) {
				tooltip.material.map.dispose();
			}
			tooltip.material.dispose();
		}
	};

	let indicator: THREE.Group | null = null;
	const addIndicator = (raycaster: THREE.Raycaster) => {
		const intersectObjects = scene.children.filter(
			(child) =>
				child.type !== 'DirectionalLight' &&
				child.type !== 'AmbientLight' &&
				child.type !== 'CameraHelper' &&
				child.type !== 'LineSegments' &&
				!child.userData.isShadowIndicator &&
				!child.userData.isLuxTooltip
		);

		const intersects = raycaster.intersectObjects(intersectObjects, true);

		if (indicator) {
			scene.remove(indicator);
			indicator.children.forEach((child) => {
				const mesh = child as THREE.Mesh;
				if (mesh.geometry) mesh.geometry.dispose();
				if (mesh.material as THREE.Material) (mesh.material as THREE.Material).dispose();
			});
			indicator = null;
		}

		if (intersects.length > 0) {
			const point = intersects[0].point;
			const intersectedObject = intersects[0].object;
			const normal = intersects[0].face?.normal?.clone();

			let isClipped = false;
			const material = (intersectedObject as THREE.Mesh).material as THREE.Material & {
				clippingPlanes?: THREE.Plane[];
			};

			if (material && Array.isArray(material.clippingPlanes)) {
				isClipped = material.clippingPlanes.some(
					(plane: THREE.Plane) => plane.distanceToPoint(point) < 0
				);
			}

			let finalClickPoint = point;
			if (isClipped) {
				const raycasterDown = new THREE.Raycaster(
					point,
					new THREE.Vector3(0, 0, -1),
					0,
					1000
				);
				const downIntersects = raycasterDown.intersectObject(intersectedObject, true);

				for (const hit of downIntersects) {
					const hitPoint = hit.point;
					const hitMaterial = (hit.object as THREE.Mesh).material as THREE.Material & {
						clippingPlanes?: THREE.Plane[];
					};
					const hitClipped = hitMaterial?.clippingPlanes?.some(
						(plane: THREE.Plane) => plane.distanceToPoint(hitPoint) < 0
					);
					if (!hitClipped) {
						finalClickPoint = hitPoint;
						break;
					}
				}
			}
			const size = 0.5;
			const thickness = 0.05;
			const indicatorMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });

			const box1 = new THREE.Mesh(
				new THREE.BoxGeometry(size, thickness, thickness),
				indicatorMaterial
			);
			const box2 = new THREE.Mesh(
				new THREE.BoxGeometry(size, thickness, thickness),
				indicatorMaterial
			);
			box2.rotation.z = Math.PI / 2; // Rotate 90 degrees to cross

			// Group them together
			indicator = new THREE.Group();
			box1.position.set(0, 0, 0);
			box2.position.set(0, 0, 0);
			indicator.add(box1);
			indicator.add(box2);

			indicator.position.copy(finalClickPoint);
			indicator.userData.isShadowIndicator = true;

			// --- Align indicator with hit normal ---
			if (normal) {
				// Transform normal to world space if needed
				if (intersectedObject.matrixWorld) {
					normal
						.applyMatrix3(
							new THREE.Matrix3().getNormalMatrix(intersectedObject.matrixWorld)
						)
						.normalize();
				}
				// Align indicator's local Z to the normal
				const quat = new THREE.Quaternion().setFromUnitVectors(
					new THREE.Vector3(0, 0, 1), // indicator's local Z
					normal
				);
				indicator.quaternion.copy(quat);
			}
			scene.add(indicator);
		}
	};

	const handleMouseMove = (_event: MouseEvent) => {
		// Skip expensive mouse move operations during camera rotation to avoid frame drops
		if (isCameraRotating()) {
			return;
		}

		// Convert mouse position to normalized device coordinates
		const rect = renderer.domElement.getBoundingClientRect();
		const mouse = new THREE.Vector2(
			((_event.clientX - rect.left) / rect.width) * 2 - 1,
			-((_event.clientY - rect.top) / rect.height) * 2 + 1
		);

		// Raycast to detect photon spheres
		const raycaster = new THREE.Raycaster();
		raycaster.setFromCamera(mouse, camera);
		const photonObjects = scene.children.filter(
			(child) => child.userData.isPhotonVisualization && child.visible
		);

		addIndicator(raycaster);
		const intersects = raycaster.intersectObjects(photonObjects, true);
		if (intersects.length > 0 && intersects[0].instanceId !== undefined) {
			const instancedMesh = intersects[0].object as THREE.InstancedMesh;
			const matrix = new THREE.Matrix4();
			instancedMesh.getMatrixAt(intersects[0].instanceId, matrix);

			const photonPosition = new THREE.Vector3();
			photonPosition.setFromMatrixPosition(matrix);

			showPhotonPathOnHover(photonPosition, scene, camera);
		} else {
			removeAllPhotonPathsFromScene(scene);
		}

		updateWindowDeletionHighlight(
			scene,
			camera,
			mouse,
			useThreeSceneStore.getState().isHoleDeletionMode
		);
		// Find sunlight visualization recursively (it's nested in a group)
		const roomSunlightVisualization: THREE.Object3D[] = [];
		const facadeSunlightVisualization: THREE.Object3D[] = [];
		const heatmapVisualization: THREE.Object3D[] = [];
		const selectedGridFill: THREE.Object3D[] = [];

		scene.traverse((child) => {
			if (
				child.userData.isRoomSunlightVisualization ||
				child.userData.isSelectedRegionVisualization
			) {
				roomSunlightVisualization.push(child);
			}

			if (child.userData.isFacadeSunlightVisualization) {
				facadeSunlightVisualization.push(child);
			}

			if (child.userData.isPhotonHeatmap) {
				heatmapVisualization.push(child);
			}

			if (child.name === 'selectedGridFill') {
				selectedGridFill.push(child);
			}
		});

		if (roomSunlightVisualization.length > 0) {
			// Set raycaster threshold for instanced mesh detection
			raycaster.params.Points = { threshold: 0.5 }; // Adjust based on your grid size

			const intersectsSunlight = raycaster.intersectObjects(roomSunlightVisualization, false);
			const intersectsSelectedFill = raycaster.intersectObjects(selectedGridFill, false);

			// Iterate through all intersections to find the first non-clipped one
			let foundVisiblePoint = false;
			let foundDetailedRegion = false;
			let hasRoomAggregation = false;

			for (const intersection of intersectsSunlight) {
				if (intersection.instanceId === undefined) continue;

				const instancedMesh = intersection.object as THREE.InstancedMesh;
				const instanceId = intersection.instanceId;

				const matrix = new THREE.Matrix4();
				instancedMesh.getMatrixAt(instanceId, matrix);
				const position = new THREE.Vector3();
				position.setFromMatrixPosition(matrix);

				const material = instancedMesh.material as THREE.Material & {
					clippingPlanes?: THREE.Plane[];
				};

				let isClipped = false;
				if (material && Array.isArray(material.clippingPlanes)) {
					// Check if point is below the clipping plane
					isClipped = material.clippingPlanes.some(
						(plane: THREE.Plane) => plane.distanceToPoint(position) < 0
					);
				}

				if (!isClipped) {
					const sunlightData = instancedMesh.userData.gridPointsData?.[instanceId];
					const isSelectedRegion = instancedMesh.userData.isSelectedRegionVisualization;
					
					if (
						useThreeSceneStore.getState().isHoleCreationMode ||
						useThreeSceneStore.getState().isHoleDeletionMode
					) {
						removeSunlightTooltip();
						return;
					}
					if (sunlightData || intersectsSelectedFill.length > 0) {
						const instanceMatrix = new THREE.Matrix4();
						instancedMesh.getMatrixAt(instanceId, instanceMatrix);

						const scale = new THREE.Vector3();
						instanceMatrix.decompose(
							new THREE.Vector3(),
							new THREE.Quaternion(),
							scale
						);

						const size = new THREE.Vector3();
						if (!instancedMesh.geometry.boundingBox) {
							instancedMesh.geometry.computeBoundingBox();
						}
						instancedMesh.geometry.boundingBox!.getSize(size);
						size.multiply(scale);

						// For selected region, pass undefined to highlight all cells in that region
						// For room visualization, pass roomName to highlight all cells in that room
						const highlightRoomName = isSelectedRegion ? undefined : sunlightData?.roomName;
						showGridPointHighlight(position, size.x, scene, highlightRoomName);
						
						if (intersectsSelectedFill.length > 0) {
							// Selected region takes precedence over room aggregation
							showSunlightTooltip(sunlightData);
							showDetailedRegionTooltip(intersectsSelectedFill[0].object, position);
							foundDetailedRegion = true;
						} else {
							// Show room aggregation when no selected region
							showSunlightTooltip(sunlightData, scene);
							foundDetailedRegion = false;
							hasRoomAggregation = !!(sunlightData.roomName && sunlightData.roomName !== 'Facade');
						}

						foundVisiblePoint = true;
						break;
					}
				}
			}

			if (!foundVisiblePoint) {
				removeSunlightTooltip();
				removeGridPointHighlight(scene);
				removeHeatmapTooltip(scene);
			}
			if (!foundDetailedRegion && !hasRoomAggregation) {
				// Only clear detailed data if we don't have room aggregation
				removeDetailedRegionTooltip();
			}
		} else if (facadeSunlightVisualization.length > 0) {
			// Check for facade sunlight visualization hover
			raycaster.params.Points = { threshold: 0.5 };

			const intersectsFacade = raycaster.intersectObjects(facadeSunlightVisualization, false);
			let foundFacadePoint = false;

			for (const intersection of intersectsFacade) {
				if (intersection.instanceId === undefined) continue;

				const instancedMesh = intersection.object as THREE.InstancedMesh;
				const instanceId = intersection.instanceId;

				const matrix = new THREE.Matrix4();
				instancedMesh.getMatrixAt(instanceId, matrix);
				const position = new THREE.Vector3();
				position.setFromMatrixPosition(matrix);

				const facadeData = instancedMesh.userData.gridPointsData?.[instanceId];
				if (
					useThreeSceneStore.getState().isHoleCreationMode ||
					useThreeSceneStore.getState().isHoleDeletionMode
				) {
					removeSunlightTooltip();
					return;
				}
				if (facadeData) {
					const instanceMatrix = new THREE.Matrix4();
					instancedMesh.getMatrixAt(instanceId, instanceMatrix);

					const scale = new THREE.Vector3();
					instanceMatrix.decompose(new THREE.Vector3(), new THREE.Quaternion(), scale);

					const size = new THREE.Vector3();
					if (!instancedMesh.geometry.boundingBox) {
						instancedMesh.geometry.computeBoundingBox();
					}
					instancedMesh.geometry.boundingBox!.getSize(size);
					size.multiply(scale);
					
					// Set facade-specific tooltip data
					if (facadeData.normal) {
						const allGridPointsData = instancedMesh.userData.gridPointsData || [];
						
						// Get the normal of the hovered point
						const hoveredNormal = new THREE.Vector3(
							facadeData.normal.x,
							facadeData.normal.y,
							facadeData.normal.z
						).normalize();
												
						// Group all facade points with similar normals (same axis AND same direction)
						// Use a very strict threshold to ensure only truly parallel walls in same direction
						const normalThreshold = 1;
						const axisPoints = allGridPointsData.filter(
							(data: { normal?: { x: number; y: number; z: number } }) => {
								if (!data.normal) return false;
								const pointNormal = new THREE.Vector3(
									data.normal.x,
									data.normal.y,
									data.normal.z
								).normalize();
								
								// Dot product close to 1 means normals point in same direction
								// Negative values mean opposite direction, so this excludes them
								const dotProduct = hoveredNormal.dot(pointNormal);
								
								// Additional check: ensure the normals are nearly identical component-wise
								const componentMatch = 
									Math.abs(hoveredNormal.x - pointNormal.x) < 0.01 &&
									Math.abs(hoveredNormal.y - pointNormal.y) < 0.01 &&
									Math.abs(hoveredNormal.z - pointNormal.z) < 0.01;
								
								const matches = dotProduct >= normalThreshold && componentMatch;
								
								return matches;
							}
						);						
						const facadeTabIndex = useDailySunlightStore.getState().facadeTabIndex;
						
						if (facadeTabIndex === 1 && axisPoints.length > 0) {
							// Show multiple highlights for all wall points on same axis
							showMultipleGridPointHighlights(axisPoints, size.x, scene);
						} else {
							// Show single point highlight with normal orientation
							showGridPointHighlight(position, size.x, scene, undefined, facadeData.normal);
						}
						
						// Set both individual point and aggregated axis data for facade tooltip
						useDailySunlightStore.getState().setHoveredFacadeData(facadeData);
						useDailySunlightStore.getState().setFacadeWallData(axisPoints);
					} else {
						// Fallback: just show individual point
						showGridPointHighlight(position, size.x, scene);
						useDailySunlightStore.getState().setHoveredFacadeData(facadeData);
						useDailySunlightStore.getState().setFacadeWallData([]);
					}
					
					foundFacadePoint = true;
					break;
				}
			}

			if (!foundFacadePoint) {
				removeSunlightTooltip();
				removeGridPointHighlight(scene);
				removeDetailedRegionTooltip();
				removeFacadeTooltip();
				removeHeatmapTooltip(scene);
			}
		} else if (heatmapVisualization.length > 0) {
			const intersectsHeatmap = raycaster.intersectObjects(heatmapVisualization, false);
			let foundHeatmapCell = false;

			for (const intersection of intersectsHeatmap) {
				if (intersection.instanceId === undefined) continue;

				const instancedMesh = intersection.object as THREE.InstancedMesh;
				const instanceId = intersection.instanceId;
				const heatmapData = instancedMesh.userData.gridPointsData?.[instanceId] as
					| PhotonHeatmapCellData
					| undefined;
				if (!heatmapData) continue;

				const matrix = new THREE.Matrix4();
				instancedMesh.getMatrixAt(instanceId, matrix);
				const position = new THREE.Vector3();
				position.setFromMatrixPosition(matrix);

				const scale = new THREE.Vector3();
				matrix.decompose(new THREE.Vector3(), new THREE.Quaternion(), scale);
				const size = new THREE.Vector3();
				if (!instancedMesh.geometry.boundingBox) {
					instancedMesh.geometry.computeBoundingBox();
				}
				instancedMesh.geometry.boundingBox!.getSize(size);
				size.multiply(scale);

				showGridPointHighlight(position, size.x, scene, undefined, heatmapData.normal);
				showHeatmapTooltip(scene, camera, position, heatmapData);
				showPhotonPathsForHeatmapCell(
					heatmapData.photonPositions,
					heatmapData.cellKey,
					scene,
					camera
				);
				removeSunlightTooltip();
				removeDetailedRegionTooltip();
				removeFacadeTooltip();
				foundHeatmapCell = true;
				break;
			}

			if (!foundHeatmapCell) {
				removeSunlightTooltip();
				removeGridPointHighlight(scene);
				removeDetailedRegionTooltip();
				removeFacadeTooltip();
				removeHeatmapTooltip(scene);
				removeAllPhotonPathsFromScene(scene);
			}
		} else {
			removeSunlightTooltip();
			removeGridPointHighlight(scene);
			removeDetailedRegionTooltip();
			removeFacadeTooltip();
			removeHeatmapTooltip(scene);
			removeAllPhotonPathsFromScene(scene);
		}
		
		const paintedAreaObjects: THREE.Object3D[] = [];
		scene.traverse((child) => {
			if (child.userData.isPaintedArea) {
				paintedAreaObjects.push(child);
			}
		});
		
		if (paintedAreaObjects.length > 0) {
			const intersectsPaintedAreas = raycaster.intersectObjects(paintedAreaObjects, false);
			if (intersectsPaintedAreas.length > 0) {
				const intersection = intersectsPaintedAreas[0];
				const intersectedMesh = intersection.object;
				const paintedAreaId = intersectedMesh.userData.paintedAreaId;
				
				const paintedAreas = useViewFeaturesStore.getState().paintedAreas;
				const paintedArea = paintedAreas.find(area => area.id === paintedAreaId);
				
				if (paintedArea && intersection.point) {
					// Calculate area from polygon points using Shoelace formula
					let areaSquareMeters = 0;
					const points = paintedArea.points;
					if (points.length >= 3) {
						for (let i = 0; i < points.length; i++) {
							const j = (i + 1) % points.length;
							areaSquareMeters += points[i].x * points[j].y;
							areaSquareMeters -= points[j].x * points[i].y;
						}
						areaSquareMeters = Math.abs(areaSquareMeters / 2);
					}
					
					const formattedArea = unitConversionService.formatWithPreferredUnit(
						areaSquareMeters,
						'area',
						'm2',
						0
					);
					
					// Create offset mouse position for tooltip (above cursor in screen space)
					const tooltipMouse = new THREE.Vector2(mouse.x, mouse.y + 0.15);
					const tooltipRaycaster = new THREE.Raycaster();
					tooltipRaycaster.setFromCamera(tooltipMouse, camera);
					
					const distanceFromCamera = Math.min(camera.position.distanceTo(intersection.point), 50);
					const tooltipPosition = new THREE.Vector3();
					tooltipPosition.copy(camera.position);
					tooltipPosition.addScaledVector(tooltipRaycaster.ray.direction, distanceFromCamera * 0.8);

					showTerrainTooltipSprite(scene, camera, tooltipPosition, paintedArea.type, formattedArea);
				} else {
					removeTerrainTooltipSprite(scene);
				}
			} else {
				removeTerrainTooltipSprite(scene);
			}
		} else {
			removeTerrainTooltipSprite(scene);
		}
	};

	const showSunlightTooltip = (sunlightData: DailySunlightResult, scene?: THREE.Scene) => {
		useDailySunlightStore.getState().setHoveredGridPointData(sunlightData);
		
		// If hovering a room grid point (not facade), aggregate all points from the same room
		if (sunlightData.roomName && sunlightData.roomName !== 'Facade' && scene) {
			const roomGridPoints: DailySunlightResult[] = [];
			
			scene.traverse((child) => {
				if (child instanceof THREE.InstancedMesh && child.userData.gridPointsData) {
					const gridPointsData = child.userData.gridPointsData as DailySunlightResult[];
					const roomPoints = gridPointsData.filter(
						(data) => data.roomName === sunlightData.roomName
					);
					roomGridPoints.push(...roomPoints);
				}
			});
			
			if (roomGridPoints.length > 1) {
				// Show aggregated room data in tab 2
				useDailySunlightStore.getState().setDetailedGridPointsData(roomGridPoints);
			} else {
				// Clear detailed data if only one point or no room
				useDailySunlightStore.getState().setDetailedGridPointsData([]);
			}
		} else {
			// Clear detailed data for non-room points (like facade)
			useDailySunlightStore.getState().setDetailedGridPointsData([]);
		}
	};

	const showDetailedRegionTooltip = (region: THREE.Object3D, position: THREE.Vector3) => {
		scene.traverse((child) => {
			if (child.userData.isSelectedRegionVisualization) {
				const detailedGridPointsData: DailySunlightResult[] = [];
				const regionBoundingBox = new THREE.Box3().setFromObject(child);
				if (!regionBoundingBox.containsPoint(position)) {
					return;
				}
				if (
					child.position.x < region.position.x - 0.1 ||
					child.position.x > region.position.x + 0.1 ||
					child.position.y < region.position.y - 0.1 ||
					child.position.y > region.position.y + 0.1
				) {
					return;
				}

				const gridPointsData = child.userData.gridPointsData as DailySunlightResult[];
				if (gridPointsData && Array.isArray(gridPointsData)) {
					for (const data of gridPointsData) {
						detailedGridPointsData.push(data);
					}
				}
				useDailySunlightStore.getState().setDetailedGridPointsData(detailedGridPointsData);
			}
		});
	};

	const removeDetailedRegionTooltip = () => {
		useDailySunlightStore.getState().setDetailedGridPointsData([]);
	};

	const removeSunlightTooltip = () => {
		useDailySunlightStore.getState().setHoveredGridPointData(null);
		// Only clear detailed data if it's not from a selected region
		// (selected region detailed data is managed by removeDetailedRegionTooltip)
		const currentDetailedData = useDailySunlightStore.getState().detailedGridPointsData;
		if (currentDetailedData.length > 0) {
			// Check if this is room aggregation data (not selected region)
			// Room aggregation would have all same roomName
			const firstRoomName = currentDetailedData[0]?.roomName;
			if (firstRoomName && firstRoomName !== 'Facade') {
				// This is room aggregation data, clear it
				useDailySunlightStore.getState().setDetailedGridPointsData([]);
			}
		}
	};

	const removeFacadeTooltip = () => {
		useDailySunlightStore.getState().setHoveredFacadeData(null);
		useDailySunlightStore.getState().setFacadeWallData([]);
	};

	let currentHighlight: THREE.Group | null = null;
	const originalRoomMaterials = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();

	const showGridPointHighlight = (
		position: THREE.Vector3,
		gridSize: number,
		scene: THREE.Scene,
		roomName?: string,
		normal?: { x: number; y: number; z: number }
	) => {
		removeGridPointHighlight(scene);

		const highlightGroup = new THREE.Group();
		highlightGroup.userData.isGridHighlight = true;
		highlightGroup.renderOrder = 1000;

		// Add highlight for the individual hovered cell
		const cellPlaneGeometry = new THREE.PlaneGeometry(gridSize, gridSize);
		const cellPlaneMaterial = new THREE.MeshBasicMaterial({
			color: 0xffffff,
			side: THREE.DoubleSide,
			transparent: true,
			opacity: 0.3,
			depthTest: false,
			depthWrite: false,
		});
		const cellPlane = new THREE.Mesh(cellPlaneGeometry, cellPlaneMaterial);
		cellPlane.position.copy(position);
		
		// Orient the highlight to match the wall surface if normal is available
		if (normal) {
			const faceNormal = new THREE.Vector3(
				normal.x,
				normal.y,
				normal.z
			).normalize();
			
			const zUp = new THREE.Vector3(0, 0, 1);
			const tangent = new THREE.Vector3().crossVectors(zUp, faceNormal);

			if (tangent.lengthSq() < 1e-6) {
				tangent.set(1, 0, 0);
			}
			tangent.normalize();

			const bitangent = new THREE.Vector3().crossVectors(faceNormal, tangent).normalize();

			const matrix = new THREE.Matrix4();
			matrix.makeBasis(tangent, bitangent, faceNormal);
			matrix.setPosition(cellPlane.position);
			
			cellPlane.matrix.copy(matrix);
			cellPlane.matrixAutoUpdate = false;
		} else {
			// Fallback for horizontal surfaces
			cellPlane.position.z += 0.025;
		}
		
		cellPlane.renderOrder = 1999;
		highlightGroup.add(cellPlane);

		currentHighlight = highlightGroup;
		scene.add(highlightGroup);

		// Highlight the room fill mesh if roomName is provided
		if (roomName) {
			const roomFillMesh = scene.getObjectByName(roomName) as THREE.Mesh;
			if (roomFillMesh) {				
				// Store original material
				if (!originalRoomMaterials.has(roomFillMesh)) {
					originalRoomMaterials.set(roomFillMesh, roomFillMesh.material);
				}

				// Create highlighted material
				const originalMaterial = roomFillMesh.material as THREE.MeshBasicMaterial;
				const highlightMaterial = originalMaterial.clone();
				// Brighten the color by mixing with white
				const originalColor = new THREE.Color(originalMaterial.color);
				const white = new THREE.Color(0xffffff);
				highlightMaterial.color = originalColor.clone().lerp(white, 0.5);
				highlightMaterial.opacity = 0.5;
				highlightMaterial.depthWrite = false;
				highlightMaterial.depthTest = false;
				// Disable clipping for the highlighted room
				highlightMaterial.clippingPlanes = [];

				roomFillMesh.material = highlightMaterial;
			}
		}
	};

	const showMultipleGridPointHighlights = (
		gridPoints: Array<{ point: THREE.Vector3; normal?: { x: number; y: number; z: number } }>,
		gridSize: number,
		scene: THREE.Scene
	) => {
		removeGridPointHighlight(scene);

		const highlightGroup = new THREE.Group();
		highlightGroup.userData.isGridHighlight = true;
		highlightGroup.renderOrder = 1000;

		// Create shared geometry and material for performance
		const cellPlaneGeometry = new THREE.PlaneGeometry(gridSize * 0.95, gridSize * 0.95);
		const cellPlaneMaterial = new THREE.MeshBasicMaterial({
			color: 0xffffff,
			side: THREE.DoubleSide,
			transparent: true,
			opacity: 0.3,
			depthTest: false,
			depthWrite: false,
		});

		// Add highlight for each grid point
		gridPoints.forEach((gridPointData) => {
			const cellPlane = new THREE.Mesh(cellPlaneGeometry.clone(), cellPlaneMaterial.clone());
			cellPlane.position.copy(gridPointData.point);
			
			// Orient the highlight to match the wall surface if normal is available
			if (gridPointData.normal) {
				const faceNormal = new THREE.Vector3(
					gridPointData.normal.x,
					gridPointData.normal.y,
					gridPointData.normal.z
				).normalize();
				
				const zUp = new THREE.Vector3(0, 0, 1);
				const tangent = new THREE.Vector3().crossVectors(zUp, faceNormal);

				if (tangent.lengthSq() < 1e-6) {
					tangent.set(1, 0, 0);
				}
				tangent.normalize();

				const bitangent = new THREE.Vector3().crossVectors(faceNormal, tangent).normalize();

				const matrix = new THREE.Matrix4();
				matrix.makeBasis(tangent, bitangent, faceNormal);
				matrix.setPosition(cellPlane.position);
				
				cellPlane.matrix.copy(matrix);
				cellPlane.matrixAutoUpdate = false;
			} else {
				// Fallback for horizontal surfaces
				cellPlane.position.z += 0.025;
			}
			
			cellPlane.renderOrder = 1999;
			highlightGroup.add(cellPlane);
		});

		currentHighlight = highlightGroup;
		scene.add(highlightGroup);
	};

	const removeGridPointHighlight = (scene: THREE.Scene) => {
		// Remove highlight geometry
		if (currentHighlight) {
			scene.remove(currentHighlight);

			currentHighlight.traverse((child) => {
				if (child instanceof THREE.Mesh) {
					if (child.geometry) {
						child.geometry.dispose();
					}
					if (child.material) {
						(child.material as THREE.Material).dispose();
					}
				}
				if (child instanceof THREE.LineSegments) {
					child.geometry.dispose();
					(child.material as THREE.Material).dispose();
				}
			});

			currentHighlight = null;
		}

		// Restore original room fill materials
		originalRoomMaterials.forEach((originalMaterial, mesh) => {
			if (mesh.material !== originalMaterial) {
				// Dispose the highlight material
				if (Array.isArray(mesh.material)) {
					mesh.material.forEach(mat => mat.dispose());
				} else {
					(mesh.material as THREE.Material).dispose();
				}
				mesh.material = originalMaterial;
			}
		});
		originalRoomMaterials.clear();
	};
	return {
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		handleMouseLeave,
		getIsRotating: isCameraRotating,
	};
};

/**
 * Creates keyboard event handlers for camera panning
 */
export const createKeyboardHandlers = (
	camera: THREE.Camera,
	controls: OrbitControls,
	config: ControlsConfig = DEFAULT_CONTROLS_CONFIG
): KeyboardHandlers => {
	const keysPressed = new Set<string>();
	let animationFrameId: number | null = null;

	const updateCameraFromKeys = () => {
		if (keysPressed.size === 0) {
			animationFrameId = null;
			return;
		}

		const panSpeed = config.keyboardPanSpeed;

		const cameraDirection = new THREE.Vector3();
		camera.getWorldDirection(cameraDirection);

		const worldForward = new THREE.Vector3(cameraDirection.x, cameraDirection.y, 0).normalize();

		const cameraRight = new THREE.Vector3();
		cameraRight.crossVectors(cameraDirection, new THREE.Vector3(0, 0, 1)).normalize();

		const moveVector = new THREE.Vector3();

		if (keysPressed.has('ArrowUp')) {
			moveVector.addScaledVector(worldForward, panSpeed);
		}
		if (keysPressed.has('ArrowDown')) {
			moveVector.addScaledVector(worldForward, -panSpeed);
		}
		if (keysPressed.has('ArrowLeft')) {
			moveVector.addScaledVector(cameraRight, -panSpeed);
		}
		if (keysPressed.has('ArrowRight')) {
			moveVector.addScaledVector(cameraRight, panSpeed);
		}

		if (moveVector.length() > 0) {
			camera.position.add(moveVector);
			controls.target.add(moveVector);

			controls.update();
		}

		animationFrameId = requestAnimationFrame(updateCameraFromKeys);
	};

	const handleKeyDown = (event: KeyboardEvent) => {
		// Don't capture keys if user is typing in an input
		if (
			event.target instanceof HTMLInputElement ||
			event.target instanceof HTMLTextAreaElement
		) {
			return;
		}

		const validKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

		if (validKeys.includes(event.code)) {
			event.preventDefault();
			keysPressed.add(event.code);

			if (animationFrameId === null) {
				animationFrameId = requestAnimationFrame(updateCameraFromKeys);
			}
		}
	};

	const handleKeyUp = (event: KeyboardEvent) => {
		keysPressed.delete(event.code);
	};

	const cleanup = () => {
		if (animationFrameId !== null) {
			cancelAnimationFrame(animationFrameId);
			animationFrameId = null;
		}
		keysPressed.clear();
	};

	return {
		handleKeyDown,
		handleKeyUp,
		cleanup,
	};
};

/**
 * Creates a resize handler for responsive camera updates
 */
export const createResizeHandler = (
	renderer: THREE.WebGLRenderer,
	camera: THREE.PerspectiveCamera,
	canvas: HTMLCanvasElement,
	canvasHeight: number
): ResizeHandler => {
	let resizeTimeout: NodeJS.Timeout | null = null;

	const handleResize = () => {
		if (resizeTimeout) clearTimeout(resizeTimeout);

		resizeTimeout = setTimeout(() => {
			const container = canvas.parentElement!;
			const newWidth = container.clientWidth;
			const newHeight = container.clientHeight || canvasHeight;

			if (newWidth > 0 && newHeight > 0) {
				renderer.setSize(newWidth, newHeight);
				camera.aspect = newWidth / newHeight;
				camera.updateProjectionMatrix();
			}
		}, 200);
	};

	return {
		handleResize,
		cleanup: () => resizeTimeout && clearTimeout(resizeTimeout),
	};
};

const updateCameraWithForma = async (camera: THREE.PerspectiveCamera): Promise<void> => {
	const formaCamera = await getFormaCamera();

	const cameraPos = new THREE.Vector3(
		formaCamera.position.x,
		formaCamera.position.y,
		formaCamera.position.z
	);
	const lookTarget = new THREE.Vector3(
		formaCamera.target.x,
		formaCamera.target.y,
		formaCamera.target.z
	);

	camera.position.copy(cameraPos);
	camera.up.set(0, 0, 1); // Z-up
	camera.lookAt(lookTarget);
};

/**
 * Complete camera control system setup
 * Returns all necessary components for camera control
 */
export interface CameraSystem {
	camera: THREE.PerspectiveCamera;
	controls: OrbitControls;
	mouseHandlers: MouseHandlers;
	keyboardHandlers: KeyboardHandlers;
	resizeHandler: ResizeHandler;
}

export const createCameraSystem = async (
	renderer: THREE.WebGLRenderer,
	scene: THREE.Scene,
	canvas: HTMLCanvasElement,
	canvasHeight: number,
	workflowProps: WorkflowProps,
	cameraConfig?: CameraConfig,
	controlsConfig?: ControlsConfig
): Promise<CameraSystem> => {
	const container = canvas.parentElement!;
	const camera = await setupCamera(container.clientWidth, canvasHeight, cameraConfig);
	const controls = setupControls(camera, renderer, controlsConfig);
	const mouseHandlers = createMouseHandlers(renderer, scene, camera, workflowProps);
	const keyboardHandlers = createKeyboardHandlers(camera, controls, controlsConfig);
	const resizeHandler = createResizeHandler(renderer, camera, canvas, canvasHeight);
	await updateCameraWithForma(camera);
	return {
		camera,
		controls,
		mouseHandlers,
		keyboardHandlers,
		resizeHandler,
	};
};
